/* global google */
// do not delete above line. Needed for google address to function

import React, { Component, useState, useEffect } from "react";
import { SF_PLANNING_SEARCH_V2 } from 'utils/constants';
import { SfFilterFileTypes, IntToString, SfAllUses } from 'utils/sf_planning_constants';
import { trackEvent, EventNames } from 'utils/mixpanel';
import { Card, Button, Form, Placeholder, Modal } from 'react-bootstrap';
import { generic_post_api } from "api/generic_api";
import GoogleMapReact from 'google-map-react';
import { truncateText } from "utils/helpers";
import { basicAccountInfo } from "api/basic_account_info";
import Joyride from 'react-joyride';

const SearchType = {
    NEW_SEARCH: 'new_search',
    FILTER: 'filter',
    NEXT_PAGE: 'next_page',
}

const search_steps = [
    {
        target: '.searchBar',
        content: 'Start with a few strong keywords that describe the type of projects and permits you are looking for.',
        disableBeacon: true,
    },
    {
        target: '.queryMethodDescriptions',
        content: 'Checkout the guide for advanced Boolean (AND/OR) queries.',
        disableBeacon: true,
    },
    {
        target: '.searchButton',
        content: 'Click on the search button to start the search.',
        disableBeacon: true,
    },
    {
        target: '.searchGuideTitle',
        content: 'Sample searches are available on the home page and when starting a new search to help you get going. And we are always a click away. Contact us for help!',
        disableBeacon: true,
    },
]

const result_row_steps = [
    {
        target: '.propertiesMap',
        content: 'You get an easy overview of the locations of the properties in the search results.',
        disableBeacon: true,
        data: 'propertiesMap',
    },
    {
        target: '.resultRow',
        content: 'A quick overview of each permit application in the search results.',
        disableBeacon: true,
    },
    {
        target: '.idLink',
        content: 'Click on the application number to view the details of this permit application.',
        disableBeacon: true,
    },
    {
        target: '.applicationSummary',
        content: 'A high level summary of the application.',
        disableBeacon: true,
    },
    {
        target: '.availableFiles',
        content: 'A list of files and documents that are available for this application. You can open/download each document in the details view of the application.',
        disableBeacon: true,
    },
    {
        target: '.allFiltersButton',
        content: 'Refine the results with multiple, powerful filters.',
        disableBeacon: true,
        data: 'allFiltersButton',
    },
]


class SfPlanningSearchV2 extends Component {

    constructor(props) {
        super(props);

        this.state = {
            last_executed_search: null,
            pages: [],
            current_page: 0,
            result_count: 0,
            search_string: "",
            start_index: 0,
            page_size: 10,
            session_id: null,
            executing_search: false,
            show_disclaimer_modal: false,
            show_search_tutorial: false,
            run_search_tutorial: false,
            result_row_tutorial: false,
            show_boolean_guide_modal: false,

            // Filter related
            file_type_filter_map: {},
            use_district_filter_map: {},
            has_opposition_filter_set: false,
            has_appeal_filter_set: false,
            zad_uses_filter_map: {},
            zad_code_sections_filter_map: {},

            effective_filter_string_list: [],
            query_error_string: null,
        };

        this.initiateNewSearch = this.initiateNewSearch.bind(this);
        this.initiateFilterSearch = this.initiateFilterSearch.bind(this);
        this.finalizeFilterSearch = this.finalizeFilterSearch.bind(this);

        this.resetFilters = this.resetFilters.bind(this);
        this.initiateNextPage = this.initiateNextPage.bind(this);
        this.finalizeNextPage = this.finalizeNextPage.bind(this);
        this.executeSearchQuery = this.executeSearchQuery.bind(this);
        this.finalizeNewSearch = this.finalizeNewSearch.bind(this);
        this.setEffectiveFilterStringList = this.setEffectiveFilterStringList.bind(this);
        this.dismissDisclaimerModal = this.dismissDisclaimerModal.bind(this);
        this.dismissBooleanGuideModal = this.dismissBooleanGuideModal.bind(this);
        this.validateSearchQuery = this.validateSearchQuery.bind(this);
        this.processFirstRunExperienceStep = this.processFirstRunExperienceStep.bind(this);
        this.setResultRowTutorial = this.setResultRowTutorial.bind(this);
        this.initiateSampleKeywordSearch = this.initiateSampleKeywordSearch.bind(this);
        this.initiateSampleBooleanSearch = this.initiateSampleBooleanSearch.bind(this);
        this.onSearchBoxKeyDown = this.onSearchBoxKeyDown.bind(this);
        this.setFileTypeFilterStartSearch = this.setFileTypeFilterStartSearch.bind(this);
        this.setUseDistrictFilterStartSearch = this.setUseDistrictFilterStartSearch.bind(this);
        this.setAppealFilterStartSearch = this.setAppealFilterStartSearch.bind(this);
        this.setOppositionFilterStartSearch = this.setOppositionFilterStartSearch.bind(this);
        this.setZadUsesFilterStartSearch = this.setZadUsesFilterStartSearch.bind(this);
        this.setZadCodeSectionsFilterStartSearch = this.setZadCodeSectionsFilterStartSearch.bind(this);
    }

    onSearchBoxKeyDown(e) {
        // User pressed the enter key
        if (e.keyCode === 13) {
            this.initiateNewSearch();
        }
    }

    componentDidMount() {
        trackEvent(EventNames.PAGE_LOAD, { 'data_1': 'planning_code_search_v2' });
        document.title = "Search Planning Permits";

        basicAccountInfo()
            .then(
                data => {
                    let show_disclaimer_modal = localStorage.getItem('show_disclaimer_modal');

                    if (show_disclaimer_modal && show_disclaimer_modal.toLocaleLowerCase() === 'true') {
                        this.setState({ show_disclaimer_modal: true });
                    }

                    let show_search_tutorial = localStorage.getItem('show_search_tutorial');

                    if (show_search_tutorial && show_search_tutorial.toLocaleLowerCase() === 'true') {
                        this.setState({ show_search_tutorial: true });
                    }
                }
            ).catch(error => {
                if (error?.name === 'LOGIN_ERROR') {
                    this.props.history.push('/login');
                }
            });
    }

    initiateNewSearch() {

        if (!this.validateSearchQuery()) {
            return;
        }

        this.setState(
            {
                session_id: null,
                start_index: 0,
                last_executed_search: SearchType.NEW_SEARCH,
                executing_search: true,
                file_type_filter_map: {},
                use_district_filter_map: {},
                has_opposition_filter_set: false,
                has_appeal_filter_set: false,
                zad_code_sections_filter_map: {},
                zad_uses_filter_map: {},
                effective_filter_string_list: [],
            }, this.executeSearchQuery);
    }

    initiateSampleKeywordSearch(sample) {
        let file_map = this.state.file_type_filter_map;
        let key_words = '';

        if (sample === 'rear_yard') {
            file_map['Variance Decision Letters'] = true;
            key_words = 'Rear yard variance for a deck';
        }
        else if (sample === 'formula_retail') {
            file_map['Motions-Executive-Summary'] = true;
            key_words = 'formula retail fitness studio';
        }
        else if (sample === 'rear_yard_zad') {
            file_map['Letters of Determination'] = true;
            key_words = 'rear yard setback';
        }
        else if (sample === 'demolish') {
            file_map['Motions-Executive-Summary'] = true;
            key_words = 'demolish a single family building';
        }
        else if (sample === 'cannabis') {
            file_map['Motions-Executive-Summary'] = true;
            key_words = 'cannabis dispensary parcel delivery service';
        }

        this.setState(
            {
                session_id: null,
                start_index: 0,
                last_executed_search: SearchType.NEW_SEARCH,
                executing_search: true,
                search_string: key_words,
                file_type_filter_map: file_map,
            }, this.executeSearchQuery);
    }

    initiateSampleBooleanSearch(sample) {
        let file_map = this.state.file_type_filter_map;
        let key_words = '';

        if (sample === 'gps') {
            file_map['Motions-Executive-Summary'] = true;
            key_words = '"GPS antenna" AND "wireless facility"';
        }
        else if (sample === 'cannabis') {
            file_map['Motions-Executive-Summary'] = true;
            key_words = 'cannabis AND ("parcel delivery service" OR "light manufacturing")';
        }

        this.setState(
            {
                session_id: null,
                start_index: 0,
                last_executed_search: SearchType.NEW_SEARCH,
                executing_search: true,
                search_string: key_words,
                file_type_filter_map: file_map,
            }, this.executeSearchQuery);
    }

    finalizeNewSearch(result_entries, start_index, session_id, result_count, aggregates) {

        let cur_pages = [];

        if (result_entries && result_entries.length > 0) {
            cur_pages.push(result_entries);
        }

        this.setState({
            pages: cur_pages,
            start_index: start_index,
            current_page: cur_pages.length - 1,
            session_id: session_id,
            executing_search: false,
            result_count: result_count,
            aggregates: aggregates,
        });
        this.setEffectiveFilterStringList();
        this.setResultRowTutorial();
    }

    setResultRowTutorial() {

        // this way, only one tutorial is enabled at a time
        let result_row_tutorial = localStorage.getItem('result_row_tutorial');

        if (result_row_tutorial && result_row_tutorial.toLocaleLowerCase() === 'true') {
            this.setState({ result_row_tutorial: true });
        }

        localStorage.setItem('show_search_tutorial', 'false');

        this.setState({
            show_search_tutorial: false,
        });
    }

    resetFilters() {
        this.setState({
            file_type_filter_map: {},
            use_district_filter_map: {},
            has_opposition_filter_set: false,
            has_appeal_filter_set: false,
            zad_code_sections_filter_map: {},
            zad_uses_filter_map: {},
            start_index: 0,
            last_executed_search: SearchType.NEW_SEARCH,
            executing_search: true,
            effective_filter_string_list: [],
        }, this.initiateNewSearch);
    }

    setEffectiveFilterStringList() {
        const getActiveKeys = (map) =>
            Object.entries(map).filter(([, v]) => v).map(([k]) => k);

        const result = [
            ...getActiveKeys(this.state.file_type_filter_map).map(k => SfFilterFileTypes[k]),
            ...getActiveKeys(this.state.use_district_filter_map),
            ...getActiveKeys(this.state.zad_code_sections_filter_map),
            ...getActiveKeys(this.state.zad_uses_filter_map),
            ...(this.state.has_opposition_filter_set ? ["Received opposition"] : []),
            ...(this.state.has_appeal_filter_set ? ["Was appealed"] : []),
        ];

        this.setState({ effective_filter_string_list: result });
    }

    // setEffectiveFilterStringList() {

    //     let result = []

    //     for (const [key, value] of Object.entries(this.state.file_type_filter_map)) {
    //         if (value === true) {
    //             result.push(SfFilterFileTypes[key]);
    //         }
    //     }

    //     for (const [key, value] of Object.entries(this.state.use_district_filter_map)) {
    //         if (value === true) {
    //             result.push(key);
    //         }
    //     }

    //     for (const [key, value] of Object.entries(this.state.zad_code_sections_filter_map)) {
    //         if (value === true) {
    //             result.push(key);
    //         }
    //     }

    //     for (const [key, value] of Object.entries(this.state.zad_uses_filter_map)) {
    //         if (value === true) {
    //             result.push(key);
    //         }
    //     }

    //     if (this.state.has_opposition_filter_set) {
    //         result.push("Received opposition");
    //     }

    //     if (this.state.has_appeal_filter_set) {
    //         result.push("Was appealed");
    //     }

    //     this.setState({ effective_filter_string_list: result });
    // }

    initiateFilterSearch() {

        if (!this.validateSearchQuery()) {
            return;
        }

        this.setState(
            {
                session_id: null,
                start_index: 0,
                last_executed_search: SearchType.FILTER,
                executing_search: true,
            }, this.executeSearchQuery);
    }

    finalizeFilterSearch(result_entries, start_index, session_id, result_count) {
        let cur_pages = [];

        if (result_entries && result_entries.length > 0) {
            cur_pages.push(result_entries);
        }

        this.setState({
            pages: cur_pages,
            start_index: start_index,
            current_page: cur_pages.length - 1,
            session_id: session_id,
            executing_search: false,
            result_count: result_count,
        });
        this.setEffectiveFilterStringList();
    }

    initiateNextPage() {
        this.setState({
            last_executed_search: SearchType.NEXT_PAGE,
            executing_search: true,
        }, this.executeSearchQuery);
    }

    finalizeNextPage(result_entries, start_index, session_id, result_count) {

        let cur_pages = this.state.pages;

        if (result_entries && result_entries.length > 0) {
            cur_pages.push(result_entries);
        }

        this.setState({
            pages: cur_pages,
            start_index: start_index,
            current_page: cur_pages.length - 1,
            session_id: session_id,
            executing_search: false,
            result_count: result_count,
        });
    }

    executeSearchQuery() {

        let file_filter = [];
        let use_district_filter = [];
        let zad_uses_filter = []
        let zad_code_sections_filter = []

        for (const [key, value] of Object.entries(this.state.file_type_filter_map)) {
            if (value === true) {
                file_filter.push(key);
            }
        }

        for (const [key, value] of Object.entries(this.state.use_district_filter_map)) {
            if (value === true) {
                use_district_filter.push(key);
            }
        }

        for (const [key, value] of Object.entries(this.state.zad_uses_filter_map)) {
            if (value === true) {
                zad_uses_filter.push(key);
            }
        }

        for (const [key, value] of Object.entries(this.state.zad_code_sections_filter_map)) {
            if (value === true) {
                zad_code_sections_filter.push(key);
            }
        }

        let request = {
            search_string: this.state.search_string,
            start_index: this.state.start_index,
            page_size: this.state.page_size,
            file_filter: file_filter,
            use_district_filter: use_district_filter,
            has_opposition_position: this.state.has_opposition_filter_set,
            has_appeal_filter: this.state.has_appeal_filter_set,
            zad_uses_filter: zad_uses_filter,
            zad_code_sections_filter: zad_code_sections_filter,
        }

        trackEvent(EventNames.SEARCH_START, { 'data_1': request });

        window.scrollTo(0, 0);

        generic_post_api(request, SF_PLANNING_SEARCH_V2)
            .then(
                result => {
                    return result.data.json()
                }
            ).then(
                data => {

                    let result_entries = data['result_entries'];
                    let start_index = data['start_index'];
                    let session_id = data['session_id'];
                    let result_count = data['total'];
                    let aggregates = data['aggregates']

                    if (this.state.last_executed_search === SearchType.NEW_SEARCH) {
                        this.finalizeNewSearch(result_entries, start_index, session_id, result_count, aggregates);
                    }
                    else if (this.state.last_executed_search === SearchType.FILTER) {
                        this.finalizeFilterSearch(result_entries, start_index, session_id, result_count);
                    }
                    else if (this.state.last_executed_search === SearchType.NEXT_PAGE) {
                        this.finalizeNextPage(result_entries, start_index, session_id, result_count);
                    }
                })
            .catch(error => {

                if (error.name === 'BAD_REQUEST') {
                    this.setState({
                        query_error_string: 'Enter a valid Boolean search expression',
                        executing_search: false,
                    });
                }
                else if (error.name === 'TIME_OUT') {
                    this.setState({
                        query_error_string: 'Something went wrong. Please try your search again.',
                        executing_search: false,
                    });
                }
            });
    }

    setFileTypeFilterStartSearch(filter, value) {
        let cur_map = { ...this.state.file_type_filter_map };
        let cur_zad_uses = { ...this.state.zad_uses_filter_map };
        let zad_code_sections = { ...this.state.zad_code_sections_filter_map };
        cur_map[filter] = value;
        
        if (filter === 'Letters of Determination' && value === false) {
            cur_zad_uses = {};
            zad_code_sections = {};
        }

        this.setState(
            { 
                file_type_filter_map: cur_map,
                zad_uses_filter_map: cur_zad_uses,
                zad_code_sections_filter_map: zad_code_sections,
            },
            this.initiateFilterSearch);
    }

    setUseDistrictFilterStartSearch(filter, value) {
        let cur_map = { ...this.state.use_district_filter_map };
        cur_map[filter] = value;

        this.setState(
            { use_district_filter_map: cur_map },
            this.initiateFilterSearch);
    }

    setAppealFilterStartSearch(filter, value) {
        this.setState(
            { has_appeal_filter_set: value },
            this.initiateFilterSearch);
    }

    setOppositionFilterStartSearch(filter, value) {
        this.setState(
            { has_opposition_filter_set: value },
            this.initiateFilterSearch);
    }

    setZadUsesFilterStartSearch(filter, value) {
        let cur_map = { ...this.state.zad_uses_filter_map };
        let file_map = { ...this.state.file_type_filter_map };

        cur_map[filter] = value;
        
        if (value === true) {
            file_map['Letters of Determination'] = true
        }

        this.setState(
            { 
                zad_uses_filter_map: cur_map,
                file_type_filter_map: file_map,
            },
            this.initiateFilterSearch);
    }

    setZadCodeSectionsFilterStartSearch(filter, value) {
        let cur_map = { ...this.state.zad_code_sections_filter_map };
        let file_map = { ...this.state.file_type_filter_map };

        cur_map[filter] = value;
        
        if (value === true) {
            file_map['Letters of Determination'] = true
        }

        this.setState(
            { 
                zad_code_sections_filter_map: cur_map,
                file_type_filter_map: file_map,
            },
            this.initiateFilterSearch);
    }

    dismissDisclaimerModal() {
        localStorage.removeItem('show_disclaimer_modal');
        this.setState({
            show_disclaimer_modal: false,
        });
    }

    processFirstRunExperienceStep(step_object, type) {

        if (step_object.status === 'finished' && type === 'search') {

            localStorage.setItem('show_search_tutorial', 'false');

            this.setState({
                show_search_tutorial: false,
                run_search_tutorial: false,
            });
        }

        else if (step_object.status === 'finished' && type === 'result_row') {

            localStorage.setItem('result_row_tutorial', 'false');

            this.setState({
                result_row_tutorial: false,
            });
        }
    }

    dismissBooleanGuideModal() {
        this.setState({ show_boolean_guide_modal: false });
    }

    validateSearchQuery() {

        this.setState({ query_error_string: null });

        let search_string = this.state.search_string;

        if (!search_string || !search_string.trim()) {
            this.setState({ query_error_string: "Enter valid search keywords" });
            return false;
        }
        return true;
    }

    render() {
        const { pages, current_page, file_type_filter_map,
            use_district_filter_map,
            last_executed_search, executing_search, start_index, aggregates,
            result_count, page_size, show_disclaimer_modal, show_search_tutorial,
            show_boolean_guide_modal, query_error_string, result_row_tutorial, run_search_tutorial,
        } = this.state;

        const pagination_page_style = (index) => {
            if (index === this.state.current_page) {
                return 'text-decoration-none pe-none fw-bold text-white';
            }
            else {
                return 'text-decoration-underline pe-auto ';
            }
        }

        const pagination_page_style_span = (index) => {
            if (index === this.state.current_page) {
                return 'me-3 fs-6 ps-1 pe-1 bg-primary';
            }
            else {
                return 'me-3 fs-6 ps-1 pe-1';
            }
        }

        const pagination_page_role = (index) => {
            if (index === this.state.current_page) {
                return '';
            }
            else {
                return 'button';
            }
        }

        const getStartIndex = () => {
            return current_page * page_size + 1;
        }

        const toggleShowMore = (filter) => {
            let curr_state = { ...this.state.filter_display_state };
            curr_state[filter] = !curr_state[filter];
            this.setState({ filter_display_state: curr_state });
        }

        const renderCheckboxGroup = (config) => {
            const {
                aggregateData,
                title,
                filterMap,
                onChangeHandler,
                idPrefix,
                labelMapper = (key) => key,  // default: just return the key
                filterCondition = () => true,  // default: include all
                toggle_value = null,
                margin_left = null,
                style = null,
            } = config;

            if (!aggregateData) return null;

            const filteredData = aggregateData.filter(entry => filterCondition(entry.key));

            if (filteredData.length === 0) return null;

            const show_more_state = this.state.filter_display_state?.[toggle_value];

            const displayItems = show_more_state
                ? filteredData
                : filteredData.slice(0, 5);

            const isChecked = (key) => {
                if (typeof filterMap === 'boolean') {
                    return filterMap;
                }
                return filterMap?.[key] || false;
            };

            return (
                <div className={`row pt-4 ${margin_left || ''}`} style={style}>
                    <div className="row mb-2">
                        <div className="col fw-semibold">{title}</div>
                    </div>
                    {
                        displayItems.map((entry) => {
                            return (
                                <div key={entry.key} className="mb-1 row">
                                    <div className="col-auto pe-0">
                                        <Form.Check
                                            name={entry.key}
                                            type="checkbox"
                                            id={`${idPrefix}-${entry.key}`}
                                            style={{ transform: 'scale(0.85)', transformOrigin: 'left center' }}
                                            checked={isChecked(entry.key)}
                                            onChange={(e) => onChangeHandler(entry.key, e.currentTarget.checked)}
                                        />
                                    </div>
                                    <div className="col ps-1 mt-1">
                                        <label htmlFor={`${idPrefix}-${entry.key}`}>
                                            <small>{labelMapper(entry.key) + " (" + entry.doc_count + ")"}</small>
                                        </label>
                                    </div>
                                </div>
                            );
                        })
                    }
                    {
                        filteredData.length > 5 && toggleShowMore &&
                        <div className="mt-2">
                            <a
                                href="#"
                                onClick={(e) => { e.preventDefault(); toggleShowMore(toggle_value); }}
                                style={{ fontSize: '0.875rem' }}
                            >
                                {show_more_state
                                    ? 'Show less'
                                    : `Show more (${filteredData.length - 5} more)`
                                }
                            </a>
                        </div>
                    }
                </div>
            );
        }

        return (
            <div class="container sfPlanningSearch">
                <Joyride
                    run={run_search_tutorial}
                    steps={search_steps}
                    showSkipButton={false}
                    showStepsProgress={true}
                    continuous={true}
                    disableScrolling={true}
                    hideBackButton={true}
                    hideCloseButton={true}
                    callback={(e) => this.processFirstRunExperienceStep(e, 'search')}
                    styles={{
                        options: {
                            arrowColor: '#efecec',
                            primaryColor: '#0D6EFD',
                            textColor: '#004a14',
                            backgroundColor: '#efecec',
                            arrowColor: '#efecec',
                        },
                    }}
                />
                <Joyride
                    run={result_row_tutorial}
                    steps={result_row_steps}
                    showSkipButton={false}
                    showStepsProgress={true}
                    continuous={true}
                    disableScrolling={true}
                    hideBackButton={true}
                    hideCloseButton={true}
                    callback={(e) => this.processFirstRunExperienceStep(e, 'result_row')}
                    styles={{
                        options: {
                            arrowColor: '#efecec',
                            primaryColor: '#0D6EFD',
                            textColor: '#004a14',
                            backgroundColor: '#efecec',
                            arrowColor: '#efecec',
                        },
                    }}
                />
                <DisclaimerModal
                    show_disclaimer_modal={show_disclaimer_modal}
                    dismissDisclaimerModal={this.dismissDisclaimerModal}
                />
                <div class="row">
                    <div class="col-3">
                        <select class="form-select" aria-label="Default select example" onChange={(e) => this.props.history.push(e.target.value)}>
                            <option disabled>Jurisdiction</option>
                            <option value="/code/sf_search" selected>San Francisco</option>
                            <option value="/code/jbk_search">Berkeley</option>
                            <option value="/code/oak_search">Oakland</option>
                        </select>
                    </div>
                    <div class="col-9">
                        <div class="input-group">
                            <input type="search"
                                placeHolder='formula retail fitness studio, "density bonus" AND (affordable OR housing)'
                                class="form-control searchBar"
                                name="search"
                                id="search"
                                disabled={executing_search}
                                value={this.state.search_string}
                                onChange={(e) => this.setState({ search_string: e.target.value })}
                                onKeyDown={(e) => this.onSearchBoxKeyDown(e)}
                            />

                            {
                                executing_search ?
                                    <Button className="bg-transparent text-dark clearButton" disabled>
                                        <span role="status">Searching&nbsp;</span>
                                        <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                    </Button>
                                    :
                                    <Button variant="warning" onClick={this.initiateNewSearch} className="searchButton">
                                        Search&nbsp;<i class="fa-solid fa-magnifying-glass fa-rotate-90"></i>
                                    </Button>
                            }

                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-3"></div>
                    <div class="col-9">
                        <div class="row">
                            <div class="col-auto">
                                <span className="tooltipAnchor ms-2 queryMethodDescriptions" role="button"
                                    onClick={() => { this.setState({ show_boolean_guide_modal: true }) }}>
                                    Search guide
                                </span>

                                <BooleanGuideModal
                                    show_boolean_guide_modal={show_boolean_guide_modal}
                                    dismissBooleanGuideModal={this.dismissBooleanGuideModal}
                                />
                            </div>
                            <div class="col">
                                {query_error_string &&
                                    <span className="text-danger">{query_error_string}</span>
                                }
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row mt-4">
                    <div class="col-3">
                        {
                            (pages && pages.length > 0) &&
                            <>
                                {
                                    renderCheckboxGroup({
                                        aggregateData: aggregates?.by_file_type,
                                        title: "Document types",
                                        filterMap: file_type_filter_map,
                                        onChangeHandler: this.setFileTypeFilterStartSearch,
                                        idPrefix: "filetype",
                                        labelMapper: (key) => SfFilterFileTypes[key],
                                        filterCondition: (key) => key in SfFilterFileTypes,
                                        toggle_value: 'by_file_type',
                                    })
                                }
                                {
                                    renderCheckboxGroup({
                                        aggregateData: aggregates?.by_use_district,
                                        title: "Use districts",
                                        filterMap: use_district_filter_map,
                                        onChangeHandler: this.setUseDistrictFilterStartSearch,
                                        idPrefix: "byUseDistrict",
                                        toggle_value: 'by_use_district',
                                    })
                                }
                                {
                                    renderCheckboxGroup({
                                        aggregateData: aggregates?.by_has_appeal,
                                        title: "Was appealed",
                                        filterMap: this.state.has_appeal_filter_set,
                                        onChangeHandler: this.setAppealFilterStartSearch,
                                        idPrefix: "wasAppealed",
                                        labelMapper: (key) => IntToString[key],
                                        filterCondition: (key) => key === 1,
                                        toggle_value: 'by_has_appeal',
                                    })
                                }
                                {
                                    renderCheckboxGroup({
                                        aggregateData: aggregates?.by_received_opposition,
                                        title: "Received opposition",
                                        filterMap: this.state.has_opposition_filter_set,
                                        onChangeHandler: this.setOppositionFilterStartSearch,
                                        idPrefix: "receivedOpposition",
                                        labelMapper: (key) => IntToString[key],
                                        filterCondition: (key) => key === 1,
                                        toggle_value: 'by_received_opposition',
                                    })
                                }

                                {
                                    (aggregates.by_zad_uses?.length > 0 ||
                                    aggregates.by_zad_code_sections?.length > 0)
                                      &&

                                    <div className="row pt-4 mb-1">
                                        <div className="col fw-bold">ZONING DETERMINATIONS</div>
                                    </div>
                                }
                                {
                                    renderCheckboxGroup({
                                        aggregateData: aggregates?.by_zad_uses,
                                        title: "Uses",
                                        filterMap: this.state.zad_uses_filter_map,
                                        onChangeHandler: this.setZadUsesFilterStartSearch,
                                        idPrefix: "zadUses",
                                        labelMapper: (key) => SfAllUses[key],
                                        filterCondition: (key) => key in SfAllUses,
                                        toggle_value: 'by_zad_uses',
                                        margin_left: 'ms-1',
                                        style: { borderLeft: '2px solid #ced4da' }
                                    })
                                }
                                {
                                    renderCheckboxGroup({
                                        aggregateData: aggregates?.by_zad_code_sections,
                                        title: "Code sections",
                                        filterMap: this.state.zad_code_sections_filter_map,
                                        onChangeHandler: this.setZadCodeSectionsFilterStartSearch,
                                        idPrefix: "zadCodeSections",
                                        toggle_value: 'by_zad_code_sections',
                                        margin_left: 'ms-1',
                                        style: { borderLeft: '2px solid #ced4da' }
                                    })
                                }

                                <div class="row mt-3">
                                    <MapComponent
                                        property_address={this.state.property_address}
                                        current_page={pages[current_page]}
                                        radius={this.state.distance_filter}
                                        pages={pages}
                                    />
                                </div>

                            </>
                        }
                    </div>
                    <div class="col-9">

                        {show_search_tutorial &&
                            <div class="container mt-4 border rounded p-3">
                                <div class="col text-start fs-5 fw-semibold">Get a quick tour of Renolition</div>
                                <div class="col text-center mt-3">
                                    <Button className="me-3" onClick={() => this.setState({ run_search_tutorial: true })}>Start tutorial</Button>
                                </div>
                            </div>
                        }

                        {
                            !last_executed_search &&
                            <GuideComponent
                                initiateSampleKeywordSearch={this.initiateSampleKeywordSearch}
                                initiateSampleBooleanSearch={this.initiateSampleBooleanSearch}
                            />
                        }
                        {
                            (
                                last_executed_search === SearchType.NEW_SEARCH || last_executed_search === SearchType.FILTER) &&
                            pages.length == 0 && !executing_search && !query_error_string &&
                            <div class="row"><div class="col fw-semibold">Your search returned no results. Adjust the keywords or filters.</div></div>
                        }
                        {
                            this.state.effective_filter_string_list && this.state.effective_filter_string_list.length > 0 &&
                            <div class="row mt-4">
                                <div class="col d-flex flex-wrap align-items-center gap-1">
                                    <span><i class="fa-solid fa-filter"></i>&nbsp;Filters</span>
                                    {
                                        this.state.effective_filter_string_list.map(filter_string => {
                                            return <span className="border p-1 rounded">{filter_string}</span>
                                        })
                                    }
                                    <span className="border p-1 rounded fw-semibold"><a role="button" onClick={this.resetFilters} className="text-decoration-none">Reset all&nbsp;<i class="fa-solid fa-xmark"></i></a></span>
                                </div>
                            </div>
                        }
                        {
                            pages[current_page] && result_count > 0 &&
                            <div class="row mt-4">
                                <div class="col text-end">
                                    Showing {getStartIndex()} - {getStartIndex() + pages[current_page].length - 1} of {result_count}
                                </div>
                            </div>
                        }
                        {pages[current_page] && pages[current_page].map(entry => {
                            if (executing_search) {
                                return <ResultRowPlaceholder />
                            }
                            else {
                                return <ResultRow entry={entry} />
                            }
                        })}

                        <div class="row mt-4">
                            <div class="col d-flex justify-content-center">
                                {
                                    pages.map((item, index) => {
                                        return <span class={pagination_page_style_span(index)}>
                                            <a className={pagination_page_style(index)} role={pagination_page_role(index)} onClick={() => this.setState({ current_page: index })}>{index + 1}</a>
                                        </span>
                                    })
                                }
                                {
                                    pages.length > 0 &&
                                    <span class="mt-1">
                                        {
                                            (result_count > start_index) &&
                                            <a className="text-decoration-none" role="button" onClick={this.initiateNextPage}>&nbsp;<i class="fa-solid fa-angle-right fa-xl"></i></a>
                                        }
                                    </span>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>)
    }
}

function ResultRow(props) {

    const { entry } = props;

    const getDetailUrl = (record_id, file_names_list) => {
        let file_names = file_names_list.join("___");
        return "/v2/code/sf_detail/" + record_id + "?fn=" + file_names;
    }

    return (
        <Card className="mt-4 resultRow">
            <Card.Body className="ps-0 pe-0" >
                <a href={getDetailUrl(entry['record_id'], entry['highlight_file_names'])} target="_blank" className="resultRowLink">
                    <div class="container" role="button">
                        <div class="row">
                            <div class="col-8">
                                <div class="row">
                                    <div class="col">
                                        <div class="row">
                                            <div class="col fs-6 fw-bold idLink">
                                                <a
                                                    className="text-black"
                                                    href={getDetailUrl(entry['record_id'], entry['highlight_file_names'])}
                                                    target="_blank">{entry['record_number']}
                                                </a>
                                            </div>
                                        </div>
                                        <div class="container mt-3 applicationSummary">
                                            <div class="row fw-lighter ">Application summary</div>
                                            <div class="row">
                                                {
                                                    entry['highlight'] && entry['highlight'].length > 0 ?
                                                        <span dangerouslySetInnerHTML={{ __html: truncateText(entry['highlight'], 400, true) }} className="p-0" /> :
                                                        <span className="p-0">{truncateText(entry['description'], 400, true)}</span>
                                                }
                                            </div>
                                        </div>
                                        <div class="container mt-3 ps-0">
                                            <div class="row fw-lighter">
                                                <div class="col-7">Application status</div>
                                                <div class="col">Application date</div>
                                            </div>
                                            <div class="row">
                                                <div class="col-7">{entry['current_status']}</div>
                                                <div class="col">{entry['application_date']}</div>
                                            </div>
                                        </div>

                                    </div>
                                </div>

                                {
                                    entry['file_types']?.length > 0 &&
                                    <div class="container mt-3 availableFiles">
                                        <div class="row fw-lighter ">Available documents</div>
                                        <div class="row">
                                            {
                                                entry['file_types']
                                                .filter(file_type => file_type in SfFilterFileTypes)
                                                .map((file_type, idx, arr) => {
                                                    if (idx < arr.length - 1) {
                                                        return <>{SfFilterFileTypes[file_type]}&nbsp;&nbsp;&#x2022;&nbsp;&nbsp; </>
                                                    }
                                                    else {
                                                        return <>{SfFilterFileTypes[file_type]}</>
                                                    }
                                                })
                                            }
                                        </div>
                                    </div>
                                }
                               
                                {
                                    (entry['zad_uses']?.length > 0 || entry['zad_code_sections']?.length > 0) &&
                                    <div class="container mt-3 availableFiles  ps-0">
                                        <div class="row fw-lighter">
                                            <div class="col-7">Applicable uses</div>
                                            <div class="col">Applicable code sections</div>
                                        </div>
                                         <div class="row">
                                            <div class="col-7">
                                                {
                                                        entry['zad_uses']?.length > 0
                                                        ? entry['zad_uses']
                                                            .map((zad_use, idx, arr) => {
                                                                if (idx < arr.length - 1) {
                                                                    return <>{zad_use}&nbsp;&nbsp;&#x2022;&nbsp;&nbsp; </>
                                                                }
                                                                else {
                                                                    return <>{zad_use}</>
                                                                }
                                                            })
                                                        : <>-</>
                                                    }
                                            </div>
                                            <div class="col">
                                                {
                                                    entry['zad_code_sections']?.length > 0
                                                    ? entry['zad_code_sections']
                                                        .map((zad_code, idx, arr) => {
                                                            if (idx < arr.length - 1) {
                                                                return <>{zad_code}&nbsp;&nbsp;&#x2022;&nbsp;&nbsp; </>
                                                            }
                                                            else {
                                                                return <>{zad_code}</>
                                                            }
                                                        })
                                                    : <>-</>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                }
                            </div>
                            <div class="col-4 propertyInfoColumn">
                                {(entry['address']['street_name'] && entry['address']['street_name'].toLocaleLowerCase() !== 'public') &&
                                    <div class="container">
                                        <div class="row">{entry['address']['street_number']} {entry['address']['street_name']} {entry['address']['street_suffix']}</div>
                                        <div class="row">San Francisco {entry['address']['postal']}</div>                                </div>
                                }
                                {
                                    (entry['use_district'] || entry['height_district']) &&
                                    <div class="container mt-3">
                                        <div class="row fw-lighter">
                                            <div class="col ps-0">Use (Zone)</div>
                                            <div class="col">Height district</div>
                                        </div>
                                        <div class="row">
                                            <div class="col ps-0"> {entry['use_district']}</div>
                                            <div class="col">{entry['height_district']}</div>
                                        </div>

                                    </div>
                                }
                                {
                                    entry['special_use_districts'] && entry['special_use_districts'].length > 0 &&
                                    <div class="container mt-3">
                                        <div class="row fw-lighter">Special use districts</div>
                                        <div class="row">
                                            {
                                                entry['special_use_districts'].map((district, idx) => {
                                                    if (idx < entry['special_use_districts'].length - 1) {
                                                        return <>{district}&nbsp;&nbsp;&#x2022;&nbsp;&nbsp; </>
                                                    }
                                                    else {
                                                        return <>{district}</>
                                                    }
                                                })
                                            }
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </a>
            </Card.Body>
        </Card>
    )
}

function ResultRowPlaceholder() {

    return (
        <Card className="mt-4 resultRow">
            <Card.Body className="ps-0 pe-0">
                <div class="container">
                    <div class="row">
                        <div class="col-8">
                            <div class="row">
                                <div class="col">
                                    <div class="row ms-1">
                                        {/* <div class="col fs-6 fw-bold idLink"> */}
                                        <Placeholder as="p" animation="glow">
                                            <Placeholder xs={4} bg="secondary" />
                                        </Placeholder>
                                        {/* </div> */}
                                    </div>
                                    <div class="container mt-3 applicationSummary">
                                        {/* <div class="row fw-lighter ">Application summary</div> */}
                                        <div class="row">
                                            <Placeholder as="p" animation="glow">
                                                <Placeholder xs={12} bg="secondary" size="lg" />
                                                <Placeholder xs={12} bg="secondary" size="lg" />
                                                {/* <Placeholder xs={12} bg="secondary"/> */}
                                            </Placeholder>
                                        </div>
                                    </div>


                                </div>
                            </div>

                            <div class="container mt-3 subApplications">

                            </div>

                            <div class="container mt-3 availableFiles">
                                <div class="row">
                                    <Placeholder as="p" animation="glow">
                                        <Placeholder xs={12} bg="secondary" size="lg" />
                                        <Placeholder xs={12} bg="secondary" size="lg" />
                                    </Placeholder>
                                </div>
                            </div>

                        </div>
                        <div class="col-4 propertyInfoColumn">

                            <div class="container">
                                <div class="row">
                                    <Placeholder as="p" animation="glow">
                                        <Placeholder xs={12} bg="secondary" />
                                        <Placeholder xs={12} bg="secondary" />
                                    </Placeholder>
                                </div>
                            </div>

                            <div class="container mt-3">
                                <div class="row">
                                </div>

                            </div>

                        </div>
                    </div>
                </div>
            </Card.Body>
        </Card>
    )
}

function DisclaimerModal(props) {

    const { show_disclaimer_modal, dismissDisclaimerModal } = props;

    return (
        <Modal
            show={show_disclaimer_modal}
            onHide={dismissDisclaimerModal}
            size="lg"
            backdrop="static"
            aria-labelledby="contained-modal-title-vcenter"
            centered>

            <Modal.Header>
                <Modal.Title>Welcome</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div class="container">
                    <div class="row">
                        <div class="col">
                            <p>
                                Thank you for signing up for Renolition, the most comprehensive tool for searching past entitlement permits.
                            </p>
                            <p>
                                Renolition indexes over <b>60K entitlement permit applications</b> and over <b>200K related documents</b> and
                                files, spanning over 10 years of data in San Francisco. New data is updated daily, and
                                we continue adding more historical data.
                            </p>
                            <p>
                                We strive to maintain an up-to-date database of permits, related documents, and files. However,
                                despite our best efforts, some records may be missing, mislabeled, or contain discrepancies
                                compared to official city records.
                            </p>
                            <p>
                                This tool is intended to assist with entitlement research but should not be relied upon for final
                                project decisions. Always consult official sources for the most accurate and authoritative codes,
                                regulations, and guidelines.
                            </p>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col text-center">
                            <Button variant="primary" onClick={dismissDisclaimerModal}>Let's go</Button>
                        </div>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    )
}

function BooleanGuideModal(props) {
    const { show_boolean_guide_modal, dismissBooleanGuideModal } = props;

    return (
        <Modal
            show={show_boolean_guide_modal}
            onHide={dismissBooleanGuideModal}
            size="lg"
            aria-labelledby="contained-modal-title-vcenter"
            centered>

            <Modal.Header closeButton>
                <Modal.Title>Search Guide</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div class="container">
                    <p>You can search in two ways:</p>

                    <div class="mb-4">
                        <div class="fw-semibold fs-5">1. Keyword Search <small class="text-muted">(Simple)</small></div>
                        <p>Enter a list of keywords separated by spaces. The search will return results that match <strong>any</strong> of the keywords.</p>
                        <p><strong>Example:</strong><br></br><code>affordable housing zoning</code><br></br>
                            → Matches records containing <em>any</em> of these terms, including misspellings and variations of the words.
                        </p>
                        <p class="mb-0">Use this for quick, broad searches.</p>
                    </div>
                    <hr />

                    <div>
                        <div class="fw-semibold fs-5">2. Boolean Search <small class="text-muted">(Advanced)</small></div>
                        <p>Use Boolean expressions for precise control. <strong>Operators are case-sensitive</strong>.</p>
                        <ul>
                            <li><strong>AND</strong> – Match all terms: <code>affordable AND housing</code></li>
                            <li><strong>OR</strong> – Match any term: <code>affordable OR housing</code></li>
                            <li><strong>NOT</strong> – Exclude a term: <code>affordable NOT housing</code></li>
                            <li><strong>Phrases</strong> – Use quotes: <code>"affordable housing"</code></li>
                            <li><strong>Wildcards</strong> – Use <code>*</code> for partial matches: <code>hous*</code></li>
                            <li><strong>Grouping</strong> – Combine logic: <code>"density bonus" AND (affordable OR housing)</code></li>
                        </ul>
                        <p class="mb-0">Use this for targeted, complex queries.</p>
                    </div>

                </div>

            </Modal.Body>
        </Modal>
    )
}

function GuideComponent(props) {
    const { initiateSampleKeywordSearch, initiateSampleBooleanSearch } = props;

    const [expanded, setExpanded] = useState(false);

    return (
        <div class="container mt-4 border round p-3">
            <div class="row"><div class="col fw-semibold fs-5 searchGuideTitle">Sample Searches</div></div>

            <div class="row mt-3">
                <div class="col">
                    <p>
                        Start with a few strong <b>keywords</b> that describe the projects you are looking for.
                        You can include the permit type (e.g. <i>Variance, Conditional Use</i>) in the keywords if
                        you know it. After the initial search results appear, you can apply filters to narrow them
                        down further.
                    </p>
                    <p>Try some of these sample searches that also set useful filters:</p>
                    <p>
                        <ul>
                            <li className="mt-2"><b>Search 1</b>: Find applications related to demolishing a single family building, that include staff letters/commission reports
                                <ul>
                                    <li>Search keywords: demolish a single family building</li>
                                    <li>Available documents filter: Comm. Motions & Executive Summaries</li>
                                    <li><a role="button" className="text-decoration-underline" onClick={() => initiateSampleKeywordSearch('demolish')}>Try it out</a></li>
                                </ul>
                            </li>
                            <li className="mt-2"><b>Search 2</b>: Find conditional use applications that contain both phrases "GPS antenna" and "wireless facility"
                                <ul>
                                    <li>Search expression: "GPS antenna" AND "wireless facility"</li>
                                    <li>Available documents filter: Comm. Motions & Executive Summaries</li>
                                    <li><a role="button" className="text-decoration-underline" onClick={() => initiateSampleBooleanSearch('gps')}>Try it out</a></li>
                                </ul>

                            </li>
                            <li className="mt-2"><b>Search 3</b>: Find conditional use applications that contain the word <i>cannabis</i> and either of the phrases "parcel delivery service" or "light manufacturing"
                                <ul>
                                    <li>Search expression: cannabis AND ("parcel delivery service" OR "light manufacturing")</li>
                                    <li>Available documents filter: Comm. Motions & Executive Summaries</li>
                                    <li><a role="button" className="text-decoration-underline" onClick={() => initiateSampleBooleanSearch('cannabis')}>Try it out</a></li>
                                </ul>
                            </li>
                        </ul>
                    </p>


                </div>
            </div>
        </div>
    );
}

// need this reference to delete the circle when creating a new one. Could not find a better method.
let my_circle = null;
function MapComponent(props) {

    const [googleApiObj, setIsGoogleApiLoadedObj] = useState(null);
    const [active_marker, setActiveMarker] = useState(null);

    const { property_address, current_page, radius, pages } = props;

    useEffect(() => {
        if (googleApiObj) {
            const { map, maps } = googleApiObj;

            if (my_circle) {
                my_circle.setMap(null);
            }
            my_circle = getCircle(map, maps)
        }
    }, [googleApiObj, property_address, radius]);

    // re-center and re-zoom after the page changes. JSON.stringify is needed for cases when
    // the size of the array changes (e.g. going to the next page).
    useEffect(() => {
        if (googleApiObj) {
            const { map, maps } = googleApiObj;
            map.setZoom(11);
            map.setCenter({ lat: 37.7617669, lng: -122.4473969 });
        }
    }, [JSON.stringify(pages), pages]);

    const reSetActiveMarker = (marker) => {
        setActiveMarker(marker);
    }

    const onChildClick = (e, childProps) => {
        setActiveMarker(childProps);
    }

    const getCircle = (map, maps) => {

        let circle_radius = 0;
        let circle_center = { lat: 37.7617669, lng: -122.4473969 }

        if (property_address && property_address.lat && property_address.lng) {
            circle_center = { lat: property_address.lat, lng: property_address.lng };
        }

        if (parseFloat(radius) > 0) {
            circle_radius = parseFloat(radius) * 1609.34;
        }

        return new maps.Circle({
            strokeColor: '#858080',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#ccc9c9',
            fillOpacity: 0.5,
            map,
            center: circle_center,
            radius: circle_radius,
        })
    }

    return (
        <div class="container propertiesMap">
            <Card className="mt-3">
                <Card.Body>
                    <div class="row">
                        <div class="col-12 map p-0">
                            <GoogleMapReact
                                bootstrapURLKeys={{ key: 'AIzaSyAyQMpPy1QPIieWCFecN0j8QGQpKTs82vk' }}
                                defaultCenter={{ lat: 37.7617669, lng: -122.4473969 }}
                                defaultZoom={11}
                                zoom={11}
                                onChildClick={onChildClick}
                                yesIWantToUseGoogleMapApiInternals={true}
                                onGoogleApiLoaded={({ map, maps }) => setIsGoogleApiLoadedObj({ map, maps })}
                            >
                                {
                                    current_page && current_page.map(entry => {
                                        return <ResultsMarker
                                            lat={entry.lat}
                                            lng={entry.lng}
                                            record_number={entry.record_number}
                                            record_id={entry.record_id}
                                            record_type={entry.record_type}
                                            description={entry.description}
                                            text="My Marker" />
                                    })
                                }
                                {active_marker &&
                                    <MapMarker
                                        record_id={active_marker.record_id}
                                        record_number={active_marker.record_number}
                                        description={active_marker.description}
                                        record_type={active_marker.record_type}
                                        setActiveMarker={reSetActiveMarker}
                                    />
                                }
                                {
                                    property_address && property_address.lat && property_address.lng &&
                                    <PropertyMarker
                                        lat={property_address.lat}
                                        lng={property_address.lng}
                                        text="Marker" />
                                }
                            </GoogleMapReact>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
}

const ResultsMarker = (props) => {

    return (
        <div class="places" >
            <div class="placesIcon"> <i class="fa-solid fa-location-dot fa-xl" style={{ color: "#d81313" }}></i></div>
        </div>

    )
}

const PropertyMarker = (props) => {

    return (
        <div class="places" >
            <div class="placesIcon"> <i class="fa-solid fa-location-dot fa-2xl" style={{ color: "#00bde3" }}></i></div>
        </div>

    )
}

const MapMarker = (props) => {

    const getDetailUrl = (record_id) => {
        return "/code/sf_detail/" + record_id;
    }
    const infoWindowStyle = {
        position: 'relative',
        top: '-90px',
        left: '-120px',
        width: 200,
        backgroundColor: 'white',
        boxShadow: '0 2px 7px 1px rgba(0, 0, 0, 0.3)',
        padding: 10,
        fontSize: 14,
        zIndex: 100,
    };

    const infoWindowClose = {
        float: 'right',
        fontSize: 11,
    };

    return (
        <>
            <div style={infoWindowStyle}>
                <div class="fw-light container gx-0">
                    <div class="row" onClick={() => props.setActiveMarker(null)}>
                        <div class="col-10 fw-bold"><small>{props.record_number}</small></div>
                        <div class="col-2 text-end" onClick={() => props.setActiveMarker(null)} role="button"><i class="fa-solid fa-x fa-lg"></i></div>
                    </div>
                    {/* <div class="row mt-1"><div class="col"><small>{props.record_type}</small></div></div> */}
                    <div class="row mt-1"><div class="col"><small>{truncateText(props.description, 80, true)}</small> </div></div>
                    <div class="row mt-1"><div class="col"><small><a role="button" target="_blank" href={getDetailUrl(props.record_id)} className="text-decoration-underline">more <i class="fa-solid fa-arrow-up-right-from-square"></i></a></small> </div></div>
                </div>
            </div>
        </>
    );
}

SfPlanningSearchV2.displayName = 'SfPlanningSearchV2';

export default SfPlanningSearchV2;